import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  InputAdornment,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { ArrowLeft, Info, UserPlus, Eye, EyeOff } from 'lucide-react';
import { adminApi, countriesApi } from '../../../api';
import useUIStore from '../../../store/useUIStore';

const UserCreatePage = ({
  sess = { uid: '', role: '' },
  clist = [],
  opcatlist = [],
}) => {
  const navigate = useNavigate();
  const [countries, setCountries] = useState(clist);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    mobile: '',
    password: '',
    country: '82',
    role: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const { showSuccess, showError } = useUIStore();

  const isAdminOrSuperAdmin = sess.role === 'admin' || sess.role === 'superadmin';

  useEffect(() => {
    const loadCountries = async () => {
      if (clist.length) return;
      try {
        const response = await countriesApi.getCountryCodes();
        const result = response?.data?.data || response?.data || [];
        setCountries(Array.isArray(result) ? result : []);
      } catch (error) {
        showError(error?.response?.data?.error || 'Failed to load countries');
      }
    };

    loadCountries();
  }, [clist.length, showError]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.username.trim()) {
      newErrors.username = 'Username is required';
    } else if (formData.username.length < 3) {
      newErrors.username = 'Username must be at least 3 characters';
    }

    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!emailPattern.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }

    if (!formData.mobile.trim()) {
      newErrors.mobile = 'Mobile number is required';
    } else if (formData.mobile.length < 9 || formData.mobile.length > 14) {
      newErrors.mobile = 'Invalid mobile number (9-14 digits)';
    }

    if (!formData.password.trim()) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }

    if (!formData.role) {
      newErrors.role = 'Please select a role';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      await adminApi.createUser({
        name: formData.username,
        email: formData.email,
        mobile: formData.mobile,
        password: formData.password,
        country: formData.country,
        role: formData.role,
      });
      showSuccess('User created successfully!');
      navigate('/admin/users');
    } catch (error) {
      console.error('Error creating user:', error);
      showError(error?.response?.data?.error || 'Error creating user');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        justifyContent="space-between"
        spacing={2}
        mb={2}
      >
        <Button variant="outlined" startIcon={<ArrowLeft size={16} />} onClick={() => navigate('/admin/users')}>
          Back to Users
        </Button>
        <Typography variant="h5">Create New User</Typography>
      </Stack>

      <Grid container spacing={2}>
        <Grid item xs={12} lg={8}>
          <Card>
            <CardContent>
              <Box component="form" onSubmit={handleSubmit}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Username"
                      name="username"
                      value={formData.username}
                      onChange={handleInputChange}
                      error={!!errors.username}
                      helperText={errors.username}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      error={!!errors.email}
                      helperText={errors.email}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      select
                      label="Country Code"
                      name="country"
                      value={formData.country}
                      onChange={handleInputChange}
                      fullWidth
                    >
                      <MenuItem value="">Select country</MenuItem>
                      {countries.map((country) => (
                        <MenuItem key={country.c_id} value={country.c_id}>
                          {country.countryName}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Mobile Number"
                      name="mobile"
                      value={formData.mobile}
                      onChange={handleInputChange}
                      error={!!errors.mobile}
                      helperText={errors.mobile}
                      fullWidth
                      inputProps={{ maxLength: 14 }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Password"
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      value={formData.password}
                      onChange={handleInputChange}
                      error={!!errors.password}
                      helperText={errors.password || 'Minimum 8 characters'}
                      fullWidth
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton onClick={() => setShowPassword((prev) => !prev)} edge="end">
                              {showPassword ? <Eye size={16} /> : <EyeOff size={16} />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      select
                      label="Role"
                      name="role"
                      value={formData.role}
                      onChange={handleInputChange}
                      error={!!errors.role}
                      helperText={errors.role}
                      fullWidth
                    >
                      <MenuItem value="">Select Role</MenuItem>
                      {isAdminOrSuperAdmin && (
                        <>
                          <MenuItem value="admin">Admin</MenuItem>
                          <MenuItem value="opadmin">Operator Admin</MenuItem>
                        </>
                      )}
                      <MenuItem value="operator">Operator</MenuItem>
                      <MenuItem value="free">Free User</MenuItem>
                    </TextField>
                  </Grid>
                </Grid>

                <Alert icon={<Info size={16} />} severity="info" sx={{ mt: 3 }}>
                  <strong>Note:</strong> The user will receive login credentials via email.
                </Alert>

                <Stack direction="row" spacing={2} mt={3}>
                  <Button
                    type="submit"
                    variant="contained"
                    startIcon={!loading && <UserPlus size={16} />}
                    disabled={loading}
                  >
                    {loading ? 'Creating...' : 'Create User'}
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => navigate('/admin/users')}
                    disabled={loading}
                  >
                    Cancel
                  </Button>
                </Stack>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} lg={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Role Permissions
              </Typography>
              <Stack component="ul" spacing={1} sx={{ pl: 2 }}>
                <li>
                  <strong>Admin:</strong> Full access to all features and settings
                </li>
                <li>
                  <strong>Operator Admin:</strong> Manage operators and customers
                </li>
                <li>
                  <strong>Operator:</strong> Handle customer interactions
                </li>
                <li>
                  <strong>Free User:</strong> Limited access
                </li>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default UserCreatePage;
